import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getLevelInfo } from "@/lib/levelUtils";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface SelfAssessmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  organizationId?: string;
  onComplete: () => void;
}

const LEVEL_GROUPS = [
  { levels: [1, 2], key: "1-2" },
  { levels: [3, 4], key: "3-4" },
  { levels: [5, 6], key: "5-6" },
  { levels: [7, 8], key: "7-8" },
  { levels: [9, 10], key: "9-10" },
];

const LEVEL_TO_ELO: Record<number, number> = { 1:700, 2:850, 3:950, 4:1050, 5:1150, 6:1250, 7:1350, 8:1450, 9:1550, 10:1650 };

export default function SelfAssessmentModal({ open, onOpenChange, userId, organizationId, onComplete }: SelfAssessmentModalProps) {
  const { t } = useTranslation("common");
  const [selected, setSelected] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState<"group" | "exact">("group");
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  const handleGroupSelect = (key: string) => {
    setSelectedGroup(key);
    setStep("exact");
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ level: selected, level_assessed_at: new Date().toISOString(), level_source: "self_assessment" } as any)
      .eq("id", userId);

    if (error) {
      toast.error("Kunde inte spara nivå");
    } else {
      // Create initial Elo rating
      if (organizationId) {
        await supabase.from("player_ratings").upsert({
          user_id: userId,
          organization_id: organizationId,
          rating: LEVEL_TO_ELO[selected] ?? 1000,
          matches_played: 0,
        } as any, { onConflict: "user_id,organization_id" });
      }
      toast.success("Nivå sparad!");
      onComplete();
      onOpenChange(false);
    }
    setSaving(false);
  };

  const activeGroup = LEVEL_GROUPS.find((g) => g.key === selectedGroup);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("levelInfo.welcomeTitle", "Välkommen! 🎾")}</DialogTitle>
          <DialogDescription>
            {t("levelInfo.welcomeDesc", "Hur skulle du bedöma din padelnivå? Detta hjälper oss matcha dig med rätt spelare.")}
          </DialogDescription>
        </DialogHeader>

        {step === "group" ? (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Välj den kategori som bäst beskriver dig</Label>
            {LEVEL_GROUPS.map((group) => {
              const info = getLevelInfo(group.levels[0]);
              const isSelected = selectedGroup === group.key;
              return (
                <button
                  key={group.key}
                  onClick={() => handleGroupSelect(group.key)}
                  className={cn(
                    "w-full flex items-center gap-3 rounded-lg border p-4 text-left transition-all",
                    isSelected
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border hover:border-primary/40 hover:bg-muted/50"
                  )}
                >
                  <div className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-2 font-bold text-sm",
                    info.bgClass, info.textClass
                  )}>
                    {group.key}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{t(`levelInfo.group${group.key.replace("-", "")}`)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            <Button variant="ghost" size="sm" onClick={() => { setStep("group"); setSelected(null); }}>
              ← {t("back")}
            </Button>
            <Label className="text-xs text-muted-foreground">{t("levelInfo.chooseExact", "Välj din exakta nivå")}</Label>
            <div className="space-y-2">
              {activeGroup?.levels.map((level) => {
                const info = getLevelInfo(level);
                const isSelected = selected === level;
                return (
                  <button
                    key={level}
                    onClick={() => setSelected(level)}
                    className={cn(
                      "w-full flex items-center gap-3 rounded-lg border p-4 text-left transition-all",
                      isSelected
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border hover:border-primary/40 hover:bg-muted/50"
                    )}
                  >
                    <div className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-2 font-bold text-lg",
                      info.bgClass, info.textClass
                    )}>
                      {level}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t(`levelInfo.${info.labelKey}`)} — {t("level")} {level}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Senare</Button>
          {step === "exact" && (
            <Button onClick={handleSave} disabled={!selected || saving}>
              {saving ? <Loader2 className="animate-spin h-4 w-4" /> : "Spara"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
